import React from 'react';
import {
  TableCell,
  Box,
  Chip,
  Typography,
  Tooltip,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import { Edit, Delete, QrCode2 } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';
import StyledTableRow from '../../../../../app/components/StyledTableRow';
import { RestaurantTable, TableStatus } from '../../types';
import { useAppDispatch } from '../../../../../app/store/configureStore';
import { updateTableStatusAsync } from '../../store/tableSlice';
import { toast } from 'react-toastify';
import { getTableStatusKey } from '../../utils/tableStatus';

interface Props {
  table: RestaurantTable;
  restaurantId: string;
  index: number;
  loading: boolean;
  target: string | null;
  onEdit?: (table: RestaurantTable) => void;
  onDelete?: (id: string) => void;
  onQrCode?: (table: RestaurantTable) => void;
}

const getStatusChipColor = (status: TableStatus) => {
  switch (status) {
    case TableStatus.Available: // 0
      return 'success';
    case TableStatus.Occupied: // 1
      return 'error';
    case TableStatus.Reserved: // 2
      return 'warning';
    default:
      return 'default';
  }
};

export const TableRow = React.memo(
  ({
    table,
    restaurantId,
    index,
    loading,
    target,
    onEdit = undefined,
    onDelete = undefined,
    onQrCode = undefined,
  }: Props) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [statusLoading, setStatusLoading] = React.useState(false);

    const handleEdit = React.useCallback(() => {
      onEdit?.(table);
    }, [onEdit, table]);

    const handleDelete = React.useCallback(() => {
      onDelete?.(table.id);
    }, [onDelete, table.id]);

    const handleQrCode = React.useCallback(() => {
      onQrCode?.(table);
    }, [onQrCode, table]);

    const handleStatusMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      setAnchorEl(event.currentTarget);
    };

    const handleStatusMenuClose = () => {
      setAnchorEl(null);
    };

    const handleStatusChange = async (newStatus: TableStatus) => {
      handleStatusMenuClose();
      if (newStatus === table.status) return;

      setStatusLoading(true);
      try {
        await dispatch(
          updateTableStatusAsync({
            restaurantId,
            tableId: table.id,
            status: newStatus,
          })
        ).unwrap();
        toast.success(t('table.status.update.success'));
      } catch (error: unknown) {
        if (error instanceof AxiosError) {
          toast.error(error.response?.data?.title || t('common.error'));
        } else {
          toast.error(t('common.error'));
        }
      } finally {
        setStatusLoading(false);
      }
    };

    return (
      <StyledTableRow
        key={table.id}
        sx={{
          '&:last-child td, &:last-child th': { border: 0 },
          width: '100%',
        }}
      >
        <TableCell width="50px" align="left" component="th" scope="row">
          {index + 1}
        </TableCell>
        <TableCell align="left">
          <Box display="flex" flexDirection="column" gap={1}>
            <Typography variant="body1">
              {t('table.list.table-number', { number: table.tableNumber })}
            </Typography>
            <Box display="flex" gap={1} alignItems="center">
              <Chip
                label={t(`table.status.${getTableStatusKey(table.status)}`)}
                color={getStatusChipColor(table.status)}
                size="small"
                onClick={handleStatusMenuOpen}
                sx={{ cursor: 'pointer' }}
              />
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleStatusMenuClose}
              >
                {[
                  TableStatus.Available,
                  TableStatus.Occupied,
                  TableStatus.Reserved,
                ].map((status) => (
                  <MenuItem
                    key={status}
                    onClick={() => handleStatusChange(status)}
                    selected={status === table.status}
                    disabled={statusLoading}
                  >
                    <Chip
                      label={t(
                        `table.status.${TableStatus[status].toLowerCase()}`
                      )}
                      color={getStatusChipColor(status)}
                      size="small"
                      sx={{ pointerEvents: 'none' }}
                    />
                  </MenuItem>
                ))}
              </Menu>
              {table.capacity && (
                <Chip
                  label={t('table.list.capacity', { seats: table.capacity })}
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
            {table.notes && (
              <Tooltip title={table.notes}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '300px',
                  }}
                >
                  {table.notes}
                </Typography>
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell align="right">
          <Box
            display="flex"
            sx={{
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            {onEdit && (
              <IconButton
                sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                onClick={handleEdit}
                size="small"
              >
                <Edit />
              </IconButton>
            )}
            {onQrCode && (
              <IconButton
                sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                onClick={handleQrCode}
                size="small"
              >
                <QrCode2 />
              </IconButton>
            )}
            {onDelete && (
              <LoadingButton
                sx={{ p: 0.25, width: '36px', minWidth: '36px' }}
                loading={loading && target === table.id}
                onClick={handleDelete}
                color="error"
                size="small"
              >
                <Delete />
              </LoadingButton>
            )}
          </Box>
        </TableCell>
      </StyledTableRow>
    );
  }
);
